import { BASE_BUBBLE_OPACITY, WHITE } from '@/components/public/Visualisations/NestedBubbles'
import { VisTreeNode } from '@/types'
import { ColorTranslator, HSLObject } from 'colortranslator'
import { HierarchyCircularNode } from 'd3-hierarchy'

export function calculateClusterSize(nodes: HierarchyCircularNode<VisTreeNode>[]) {
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  nodes.forEach((node: HierarchyCircularNode<VisTreeNode>) => {
    const left = node.x - node.r
    const right = node.x + node.r
    const top = node.y - node.r
    const bottom = node.y + node.r

    if (left < minX) minX = left
    if (right > maxX) maxX = right
    if (top < minY) minY = top
    if (bottom > maxY) maxY = bottom
  })

  const width = maxX - minX
  const height = maxY - minY
  return { width, height }
}

export function findLargestRadiiByLevel(
  node: HierarchyCircularNode<any>,
  level = 0,
  largestRadii: Map<number, number> = new Map<number, number>(),
) {
  // Check if it's a leaf node (no children)
  if (!node.children || node.children.length === 0) {
    // Update the map if this is the largest radius found at this level
    const currentLargest = largestRadii.get(level) ?? 0
    if (node.r > currentLargest) {
      largestRadii.set(level, node.r)
    }
  } else {
    // If it's not a leaf node, update the largest radius for this level and continue for each child
    const currentLargest = largestRadii.get(level) ?? 0
    if (node.r > currentLargest) {
      largestRadii.set(level, node.r)
    }
    node.children.forEach((child) => {
      findLargestRadiiByLevel(child, level + 1, largestRadii)
    })
  }

  return largestRadii
}

const perceivedBubbleColourCache: Record<string, Record<number, HSLObject>> = {}

// Nested bubbles are stacked on top of each other with the bottom most being rendered with the story
// colour and ascending levels being rendered with semi-transparent white.
// This function returns the colour of a nested bubble at a given level as seen by the user
// by calculating the cumulative effect of applying the semi-transparent white layers on top of the story colour.
export function getPerceivedBubbleColour(storyColour: string, level: number): HSLObject {
  if (perceivedBubbleColourCache[storyColour] && perceivedBubbleColourCache[storyColour][level]) {
    return perceivedBubbleColourCache[storyColour][level]
  }

  for (let currentLevel = 1; currentLevel <= level; currentLevel++) {
    if (!perceivedBubbleColourCache[storyColour]) {
      perceivedBubbleColourCache[storyColour] = {}
    }

    if (currentLevel === 1) {
      perceivedBubbleColourCache[storyColour][currentLevel] =
        ColorTranslator.toHSLObject(storyColour)
    } else {
      const previousColour = perceivedBubbleColourCache[storyColour][currentLevel - 1]
      const blendSteps = Math.round(1 / BASE_BUBBLE_OPACITY + 1)
      const blendedColour = ColorTranslator.getBlendHSLObject(previousColour, WHITE, blendSteps)[1]
      perceivedBubbleColourCache[storyColour][currentLevel] = blendedColour
    }
  }

  return perceivedBubbleColourCache[storyColour][level]
}
